import React from "react";
import { useLanguage } from "@/context/LanguageContext";
import { Users, Gift, Infinity as InfinityIcon, Check, Share2 } from "lucide-react";
import PlayStoreButton from "@/components/PlayStoreButton";

const Referral = () => {
  const { t } = useLanguage();
  return (
    <section
      id="referral"
      data-testid="referral-section"
      className="relative py-24 sm:py-28"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10">
        <div className="relative rounded-[32px] overflow-hidden bg-gradient-to-br from-amber-50 via-[#fdfbf7] to-amber-100/60 border border-amber-300/40 p-8 sm:p-12 lg:p-16">
          {/* Decorative gold blobs */}
          <div className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-amber-300/30 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 w-[380px] h-[380px] rounded-full bg-orange-200/40 blur-3xl pointer-events-none" />

          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <span
                data-testid="referral-eyebrow"
                className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.18em] uppercase text-amber-700 bg-white/70 border border-amber-300/60 px-3.5 py-1.5 rounded-full"
              >
                <Gift size={14} />
                {t.referral.eyebrow}
              </span>
              <h2
                data-testid="referral-title"
                className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-blue-950 leading-[1.05]"
              >
                {t.referral.title}
              </h2>
              <p
                data-testid="referral-subtitle"
                className="mt-5 text-base sm:text-lg text-slate-700 leading-relaxed max-w-2xl"
              >
                {t.referral.subtitle}
              </p>

              <ul className="mt-8 space-y-3">
                {[t.referral.bullet1, t.referral.bullet2, t.referral.bullet3].map(
                  (b, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-3 text-[15px] text-slate-800"
                    >
                      <span className="h-6 w-6 rounded-full bg-emerald-500 text-white inline-flex items-center justify-center mt-0.5 shrink-0">
                        <Check size={13} strokeWidth={3} />
                      </span>
                      <span>{b}</span>
                    </li>
                  )
                )}
              </ul>

              <div className="mt-9 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <PlayStoreButton
                  size="lg"
                  variant="dark"
                  testId="referral-cta-playstore"
                />
                <span className="text-sm text-slate-600 inline-flex items-center gap-2">
                  <Share2 size={16} className="text-amber-600" />
                  {t.referral.cta}
                </span>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative grid grid-cols-3 gap-3">
                <StatTile
                  Icon={Users}
                  iconBg="bg-blue-900 text-amber-400"
                  big={t.referral.stat1}
                  label={t.referral.stat1Label}
                />
                <div className="hidden sm:flex items-center justify-center text-3xl text-amber-500 font-black">
                  →
                </div>
                <StatTile
                  Icon={Gift}
                  iconBg="bg-amber-500 text-white"
                  big={t.referral.stat2}
                  label={t.referral.stat2Label}
                />
              </div>

              <div className="mt-4 rounded-2xl bg-blue-950 text-white p-5 sm:p-6 flex items-center justify-between relative overflow-hidden">
                <div className="absolute -right-12 -bottom-12 w-40 h-40 rounded-full bg-amber-500/15 blur-2xl pointer-events-none" />
                <div className="flex items-center gap-4 relative">
                  <span className="h-12 w-12 rounded-xl bg-amber-500/20 text-amber-400 inline-flex items-center justify-center">
                    <InfinityIcon size={28} strokeWidth={2.5} />
                  </span>
                  <div className="leading-tight">
                    <p
                      className="text-3xl font-black text-amber-400"
                      style={{ fontFamily: "Outfit, sans-serif" }}
                    >
                      {t.referral.stat3}
                    </p>
                    <p className="text-xs text-slate-300 mt-1 max-w-[180px]">
                      {t.referral.stat3Label}
                    </p>
                  </div>
                </div>
                <div className="relative hidden sm:flex flex-col items-end">
                  <code
                    className="text-[10px] tracking-wider text-slate-400 uppercase"
                    style={{ fontFamily: "Outfit, sans-serif" }}
                  >
                    Your code
                  </code>
                  <code
                    className="text-lg font-black text-white tracking-[0.25em]"
                    style={{ fontFamily: "Outfit, sans-serif" }}
                  >
                    4663D0
                  </code>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const StatTile = ({ Icon, iconBg, big, label }) => (
  <div className="rounded-2xl bg-white border border-amber-300/40 p-5 sm:p-6 text-center shadow-sm">
    <div
      className={`mx-auto h-11 w-11 rounded-xl ${iconBg} flex items-center justify-center`}
    >
      <Icon size={22} strokeWidth={2.2} />
    </div>
    <p
      className="mt-3 text-4xl sm:text-5xl font-black text-blue-950 tracking-tight"
      style={{ fontFamily: "Outfit, sans-serif" }}
    >
      {big}
    </p>
    <p className="mt-1 text-[11px] sm:text-xs text-slate-600 leading-tight font-semibold">
      {label}
    </p>
  </div>
);

export default Referral;
